import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Breadcrumbs,
  Flex,
  FlexColumn,
  Tab,
  Tabs,
} from '@elementary/common-ui/components';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import { numberWithCommas } from '@elementary/common-ui/utils/common';
import { useQuery } from '@tanstack/react-query';
import { PULL_REQUESTS } from '@elementary/common-ui/constants/queryKeys';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { space } from '@elementary/common-ui/style/utils';
import * as sharedApi from '../../shared/services/api';
import { PullRequestTab } from '../types';
import PullRequestsTable from './PullRequestsTable';

const Wrapper = styled(FlexColumn)`
  gap: ${space('xxxl')};
  padding: ${space('xxl')};
  height: 100%;
`;

const TabsContainer = styled(Tabs)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.subtle};
`;

const urlParamsPrefix = 'pull_requests_table_filters';
const localStorageKey = 'pull_requests_table';

const PullRequests = () => {
  const selectedEnvId = useSelectedEnv();
  const [currentTab, setCurrentTab] = useState<string>(PullRequestTab.All);
  const { urlFilters, addFilter, updateFilter, clearFilter } = useUrlFilters({
    urlKey: urlParamsPrefix,
  });

  const onCurrentTabChange = (e: React.ChangeEvent<{}>, newValue: string) => {
    setCurrentTab(newValue);

    if (newValue === PullRequestTab.All) {
      clearFilter('status', 'equals');
    } else {
      const newFilterValue =
        newValue === PullRequestTab.InProgress ? ['open'] : ['merged'];
      const curFilter = urlFilters?.find((f) => f.property === 'status');

      if (curFilter)
        updateFilter('status', 'equals', curFilter.value, newFilterValue);
      else addFilter('status', 'equals', newFilterValue);
    }
  };

  const { data: pullRequestsData, isLoading: isPullRequestsLoading } = useQuery(
    [PULL_REQUESTS, selectedEnvId],
    () => sharedApi.fetchPullRequests(selectedEnvId!),
    { enabled: !!selectedEnvId },
  );

  useViewLoadTracking({ name: 'Pull Requests', loadDeps: [pullRequestsData] });

  const pullRequestsTableData = useMemo(
    () => pullRequestsData?.pull_requests || [],
    [pullRequestsData],
  );

  const tabs = useMemo(
    () => [
      {
        value: PullRequestTab.All,
        label: 'All',
        count: pullRequestsTableData?.length,
      },
      {
        value: PullRequestTab.InProgress,
        label: 'In progress',
        count: pullRequestsTableData?.filter((pr) => pr.status === 'open')
          .length,
      },
      {
        value: PullRequestTab.Merged,
        label: 'Merged',
        count: pullRequestsTableData?.filter((pr) => pr.status === 'merged')
          .length,
      },
    ],
    [pullRequestsTableData],
  );

  return (
    <Wrapper>
      <FlexColumn gap={2}>
        <Breadcrumbs
          items={['Configuration', 'Pull Requests']}
          showTreeButton={false}
        />

        <TabsContainer value={currentTab} onChange={onCurrentTabChange}>
          {tabs.map(({ value, label, count }) => (
            <Tab
              key={value}
              label={
                <Flex sx={{ gap: 1 }}>
                  {label} ({numberWithCommas(count)})
                </Flex>
              }
              value={value}
            />
          ))}
        </TabsContainer>
      </FlexColumn>

      <PullRequestsTable
        data={pullRequestsTableData}
        loading={isPullRequestsLoading}
        urlParamsPrefix={urlParamsPrefix}
        localStorageKey={localStorageKey}
      />
    </Wrapper>
  );
};

export default PullRequests;
